import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-[var(--line)] bg-[var(--surface)] shadow-sm',
        className
      )}
      {...props}
    />
  );
}

export function CardHeader({
  className,
  title,
  description,
  actions,
  children
}: {
  className?: string;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-[var(--line)] px-6 py-4',
        className
      )}
    >
      {children ?? (
        <div className="min-w-0 space-y-0.5">
          {title ? (
            <h2 className="truncate text-base font-semibold tracking-tight">{title}</h2>
          ) : null}
          {description ? <p className="text-sm text-muted">{description}</p> : null}
        </div>
      )}
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-6', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-[var(--line)] px-6 py-4',
        className
      )}
      {...props}
    />
  );
}

/** Big single-number tile used on reports and the invoice header. */
export function StatTile({
  label,
  value,
  sub,
  loading,
  tone = 'default',
  className
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  loading?: boolean;
  tone?: 'default' | 'positive' | 'warning' | 'danger';
  className?: string;
}) {
  const toneClass = {
    default: 'text-[var(--text)]',
    positive: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    danger: 'text-red-600 dark:text-red-400'
  }[tone];

  return (
    <div
      className={cn(
        'rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 shadow-sm',
        className
      )}
    >
      <p className="text-sm text-muted">{label}</p>
      {loading ? (
        <div className="skeleton mt-2 h-8 w-28" />
      ) : (
        <p className={cn('tnum mt-1 text-2xl font-semibold tracking-tight', toneClass)}>
          {value}
        </p>
      )}
      {sub && !loading ? <p className="mt-1 text-sm text-muted">{sub}</p> : null}
    </div>
  );
}
